import type { ArrayView, IndexPointer, Tone } from '../types';
import { CANVAS, GEO, toneColor, toneFill } from '../theme';

import {
  Caption,
  EmptyCanvas,
  FONT,
  LAYOUT,
  PointerTip,
  fmt,
  frameProps,
  isNum,
  labelWidth,
  valueWidth,
} from './shared';

const CELL = GEO.cell;
const GAP = GEO.cellGap;
const PAD = GEO.padding;
const BAR_MAX = 140;
const BAR_LABEL_BAND = 18;
const WINDOW_BAND = 22;
const WINDOW_LABEL_BAND = 16;
/** Horizontal breathing room kept on each side of a cell's text. */
const CELL_TEXT_PAD = 16;

export function ArrayRenderer({ view }: { view: ArrayView }) {
  const n = view.values.length;
  const title = view.caption ?? (n === 0 ? 'Empty array' : `Array of ${n} values`);

  if (n === 0) return <EmptyCanvas title={title} />;

  const bars = view.mode === 'bars';
  const showIndices = view.showIndices !== false;

  /*
   * A cell's value is drawn centred on one line — a value wider than the
   * base 56px cell (a multi-word string, like a pipeline-stage label) would
   * otherwise overflow into the cell beside it. Widen every cell uniformly
   * to fit the widest value in the array. Short DSA-style values (single
   * numbers) never exceed the base cell width, so this is a no-op for
   * existing content.
   */
  const CW = Math.max(CELL, Math.max(0, ...view.values.map((v) => valueWidth(fmt(v)))) + CELL_TEXT_PAD);
  const inRange = (i: number) => Number.isInteger(i) && i >= 0 && i < n;
  // A pointer may also sit at exactly n — a loop that consumed every element
  // ends with its cursor off the end, and that is the termination condition
  // being taught. A mark cannot: there is no cell there to tint.
  const inPointerRange = (i: number) => Number.isInteger(i) && i >= 0 && i <= n;

  const markByIndex = new Map<number, { tone: Tone; label?: string }>();
  for (const mark of view.marks ?? []) {
    if (inRange(mark.index)) markByIndex.set(mark.index, { tone: mark.tone, label: mark.label });
  }

  // Pointers on the same index stack downwards so `slow` and `fast` never overlap.
  const pointerGroups = new Map<number, IndexPointer[]>();
  for (const pointer of view.pointers ?? []) {
    if (!inPointerRange(pointer.index)) continue;
    const group = pointerGroups.get(pointer.index) ?? [];
    group.push(pointer);
    pointerGroups.set(pointer.index, group);
  }
  const pointerDepth = Math.max(0, ...[...pointerGroups.values()].map((g) => g.length));

  const rawWindow = view.window;
  const win =
    rawWindow &&
    (() => {
      const start = Math.max(0, Math.min(n - 1, rawWindow.start));
      const end = Math.max(0, Math.min(n - 1, rawWindow.end));
      return end < start ? null : { ...rawWindow, start, end };
    })();

  const hasMarkLabels = [...markByIndex.values()].some((m) => !!m.label);

  let cursor = PAD;
  let captionY = 0;
  if (view.caption) {
    captionY = cursor + 8;
    cursor += LAYOUT.captionBand;
  }
  let windowLabelY = 0;
  let windowY = 0;
  if (win) {
    if (win.label) {
      windowLabelY = cursor + 8;
      cursor += WINDOW_LABEL_BAND;
    }
    windowY = cursor + 4;
    cursor += WINDOW_BAND;
  }
  let barLabelY = 0;
  if (bars) {
    barLabelY = cursor + 9;
    cursor += BAR_LABEL_BAND;
  }
  const rowY = cursor;
  const rowH = bars ? BAR_MAX : CELL;
  cursor += rowH;
  let markLabelY = 0;
  if (hasMarkLabels) {
    markLabelY = cursor + 8;
    cursor += LAYOUT.labelBand;
  }
  let indexY = 0;
  if (showIndices) {
    indexY = cursor + 10;
    cursor += LAYOUT.indexBand;
  }
  const pointerTop = cursor + 2;
  cursor += pointerDepth * LAYOUT.pointerRow;

  /*
   * A mark label is centred under a 56px cell, so anything longer overhangs the
   * cell on both sides — and on the first or last cell that overhang fell
   * outside the viewBox and was clipped. Reserve the overhang on both edges and
   * shift the content in by it, so authors are not forced to abbreviate labels
   * to fit an invisible limit.
   */
  const longestMarkLabel = Math.max(0, ...[...markByIndex.values()].map((m) => labelWidth(m.label)));
  const overhang = Math.max(0, Math.ceil((longestMarkLabel - CW) / 2));
  const originX = PAD + overhang;

  // Reserve the slot past the last cell when a cursor has run off the end,
  // otherwise its label would be drawn outside the viewBox and clipped.
  const endSlot = pointerGroups.has(n) ? CW + GAP : 0;
  const width = PAD * 2 + overhang * 2 + n * CW + (n - 1) * GAP + endSlot;
  const height = cursor + PAD;
  const x = (i: number) => originX + i * (CW + GAP);
  const cx = (i: number) => x(i) + CW / 2;

  const numericMax = Math.max(1, ...view.values.filter(isNum).map((v) => Math.abs(v)));

  return (
    <svg {...frameProps(width, height, view.caption)}>
      <title>{title}</title>
      {view.caption ? <Caption text={view.caption} x={PAD} y={captionY} /> : null}

      {win ? (
        <g aria-hidden="true">
          {win.label ? (
            <text
              x={(x(win.start) + x(win.end) + CW) / 2}
              y={windowLabelY}
              fill={toneColor(win.tone, 'info')}
              fontSize={FONT.label}
              textAnchor="middle"
              dominantBaseline="central"
            >
              {win.label}
            </text>
          ) : null}
          <path
            d={`M ${x(win.start)} ${windowY + 12} L ${x(win.start)} ${windowY} L ${x(win.end) + CW} ${windowY} L ${x(win.end) + CW} ${windowY + 12}`}
            fill="none"
            stroke={toneColor(win.tone, 'info')}
            strokeWidth={2}
          />
        </g>
      ) : null}

      {bars ? (
        <line
          x1={PAD}
          y1={rowY + BAR_MAX}
          x2={width - PAD}
          y2={rowY + BAR_MAX}
          stroke={CANVAS.cellBorder}
          strokeWidth={1}
          aria-hidden="true"
        />
      ) : null}

      {view.values.map((value, i) => {
        const mark = markByIndex.get(i);
        const stroke = mark ? toneColor(mark.tone) : CANVAS.cellBorder;
        const fill = mark ? toneFill(mark.tone) : CANVAS.cell;
        if (bars) {
          const magnitude = isNum(value) ? Math.abs(value) : 0;
          const h = magnitude > 0 ? Math.max(3, (magnitude / numericMax) * BAR_MAX) : 3;
          return (
            <g key={i}>
              <text
                x={cx(i)}
                y={barLabelY}
                fill={CANVAS.textDim}
                fontSize={FONT.label}
                textAnchor="middle"
                dominantBaseline="central"
              >
                {fmt(value)}
              </text>
              <rect
                x={x(i)}
                y={rowY + BAR_MAX - h}
                width={CW}
                height={h}
                rx={3}
                fill={fill}
                stroke={stroke}
                strokeWidth={1.5}
              />
            </g>
          );
        }
        return (
          <g key={i}>
            <rect
              x={x(i)}
              y={rowY}
              width={CW}
              height={CELL}
              rx={8}
              fill={fill}
              stroke={stroke}
              strokeWidth={1.5}
            />
            <text
              x={cx(i)}
              y={rowY + CELL / 2}
              fill={CANVAS.text}
              fontSize={FONT.value}
              textAnchor="middle"
              dominantBaseline="central"
            >
              {fmt(value)}
            </text>
          </g>
        );
      })}

      {hasMarkLabels
        ? [...markByIndex.entries()].map(([i, mark]) =>
            mark.label ? (
              <text
                key={`mark-${i}`}
                x={cx(i)}
                y={markLabelY}
                fill={toneColor(mark.tone)}
                fontSize={FONT.label}
                textAnchor="middle"
                dominantBaseline="central"
              >
                {mark.label}
              </text>
            ) : null,
          )
        : null}

      {showIndices
        ? view.values.map((_, i) => (
            <text
              key={`idx-${i}`}
              x={cx(i)}
              y={indexY}
              fill={CANVAS.textFaint}
              fontSize={FONT.index}
              textAnchor="middle"
              dominantBaseline="central"
            >
              {`[${i}]`}
            </text>
          ))
        : null}

      {[...pointerGroups.entries()].map(([i, group]) =>
        group.map((pointer, depth) => {
          const top = pointerTop + depth * LAYOUT.pointerRow;
          const color = toneColor(pointer.tone);
          return (
            <g key={`ptr-${i}-${pointer.name}-${depth}`}>
              <PointerTip x={cx(i)} y={top} color={color} />
              <text
                x={cx(i)}
                y={top + 13}
                fill={color}
                fontSize={FONT.label}
                textAnchor="middle"
                dominantBaseline="central"
              >
                {pointer.name}
              </text>
            </g>
          );
        }),
      )}
    </svg>
  );
}

export default ArrayRenderer;
